import { useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { GET_CONVERSATION_ASSIGNEE_GROUPS_MANAGEMENT_QUERY } from 'src/graphql/entities/conversation-assignee-group/conversation-assignee-group.operations';
import type {
  ConversationAssigneeGroupManagement,
  GetConversationAssigneeGroupsManagementQuery,
} from 'src/graphql/entities/conversation-assignee-group/conversation-assignee-group.types';
import { buildManagementQueryParams } from 'src/graphql/entities/conversation-assignee-group/conversation-assignee-group.utils';
import { usePlatformCatalogAccess } from 'src/graphql/entities/group-rules/platform-catalog-access';
import type { QueryVariables } from 'src/graphql/types';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';

export const useConversationAssigneeGroupsManagement = (
  partnerId: number | null | undefined,
) => {
  const { includePlatformLevelData } = usePlatformCatalogAccess(
    'ConversationAssigneeGroup',
    partnerId,
  );

  const params = useMemo(
    () => buildManagementQueryParams(partnerId, includePlatformLevelData),
    [partnerId, includePlatformLevelData],
  );

  const queryResult = useQuery<GetConversationAssigneeGroupsManagementQuery, QueryVariables>(
    GET_CONVERSATION_ASSIGNEE_GROUPS_MANAGEMENT_QUERY,
    {
      variables: { params: params ?? undefined },
      skip: isNullish(params),
      fetchPolicy: 'cache-and-network',
      context: {
        includePlatformLevelData,
      },
    },
  );

  const { data, loading, error, refetch } = queryResult;

  const cacheManager = useCacheManager<
    GetConversationAssigneeGroupsManagementQuery,
    QueryVariables
  >(GET_CONVERSATION_ASSIGNEE_GROUPS_MANAGEMENT_QUERY, queryResult);

  const groups: ConversationAssigneeGroupManagement[] = useMemo(
    () => data?.getConversationAssigneeGroup?.records ?? [],
    [data?.getConversationAssigneeGroup?.records],
  );

  return {
    groups,
    loading,
    error,
    refetch,
    cacheManager,
  };
};
